import { Component, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { EnumsService } from '../../../services/enums.service';
import { ConfigurableFieldHostComponent } from '../../configurable-field-host.component';
import { RealEstateOwned } from '../../../models/mortgage.model';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { syncReoMortgageLiability } from '../reo-liability-sync';
import { reoMatchesAddress } from '../../reo-address-match.util';
import { ReoLiensComponent } from '../reo-liens/reo-liens.component';

@Component({
    selector: 'reo-properties',
    templateUrl: 'reo-properties.component.html',
    standalone: false
})
export class ReoPropertiesComponent extends ConfigurableFieldHostComponent implements OnInit {

  @Input()
  mortgage: MortgageApplication;

  @Input()
  hideDateAcquired: boolean = false;

  @Input()
  set reo(reo: RealEstateOwned) {
    this._reo = reo;
    if (!this._reo.dispositionStatus) {
      this._reo.dispositionStatus = 'Retained';
    }
    this.willPropertyBeSold = (this._reo.dispositionStatus === 'PendingSale' ? 'yes' : 'no');
    if (this.mortgage.subjectProperty?.purposeOfLoan === 'Refinance') {
      this._reo.isSubjectProperty = this.isSubjectPropertyTheSameAsReo(this._reo);
      if (!this._reo.isSubjectProperty) {
        this.mortgage.subjectProperty.originalCost = undefined;
        this.mortgage.subjectProperty.originalCostYear = undefined;
      } else {
        this.mortgage.subjectProperty.originalCost = this._reo.purchasePrice;
        const year = this._reo.dateAcquired ? new Date(this._reo.dateAcquired).getFullYear() : undefined;
        this.mortgage.subjectProperty.originalCostYear = year;
      }
    }
  }

  get reo(): RealEstateOwned {
    return this._reo;
  }

  @ViewChild('reoPropertiesForm')
  reoPropertiesForm: NgForm;

  @ViewChild('reoLiens')
  reoLiens: ReoLiensComponent | undefined;

  reoStatusTypes: EnumerationItem[] = [];
  usageTypes: EnumerationItem[] = [];
  propertyTypes: EnumerationItem[] = [];

  uniqueId: number;

  protected isMobile: boolean = false;

  protected willPropertyBeSold: string = 'no';

  private _reo: RealEstateOwned = new RealEstateOwned();

  constructor(private readonly _enumsService: EnumsService) {
    super();
    this.reoStatusTypes = this._enumsService.reoStatusTypes;
    this.usageTypes = this._enumsService.usageTypes;
    this.propertyTypes = this._enumsService.reoPropertyTypes;
    this.uniqueId = Date.now() & 0xffffffff;
    this.uniqueId = this.uniqueId < 0 ? this.uniqueId * -1 : this.uniqueId;
  }

  ngOnInit() {
    this.configureBasedOnScreenSize(window.innerWidth);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.configureBasedOnScreenSize(window.innerWidth);
  }

  onPropertyWillBeSoldChanged = () => {
    if (this.willPropertyBeSold === 'no') {
      this.reo.dispositionStatus = 'Retained';
    } else {
      this.reo.dispositionStatus = 'PendingSale';
      this.reo.intendedUsageType = null;
    }
  }

  onPurchasePriceChanged = () => {
    if (!this.reo.purchasePrice) {
      this.reo.purchasePrice = undefined;
    }
    if (this._reo.isSubjectProperty) {
      this.mortgage.subjectProperty.originalCost = this.reo.purchasePrice;
    }
  }

  onDateAcquiredChanged = () => {
    if (!this.reo.dateAcquired) {
      this.reo.dateAcquired = undefined;
    }
    if (this._reo.isSubjectProperty) {
      const year = this.reo.dateAcquired ? new Date(this.reo.dateAcquired).getFullYear() : undefined;
      this.mortgage.subjectProperty.originalCostYear = year;
    }
  }

  onPropertyValueChanged = () => {
    if (!this.reo.marketValue) {
      this.reo.marketValue = undefined;
    }
  }

  onMortgagePaymentChanged = () => {
    this.consolidateReoLiabilities();
  }

  validate = (): boolean => {
    if (this.reoPropertiesForm) {
      this.reoPropertiesForm.form.markAllAsTouched();
      // LLS-569: the lien section is not a form control; Required on reo.amountOfMortgage means
      // "at least one lien", checked by the child so it can show its own message.
      const liensValid = this.reoLiens ? this.reoLiens.validate() : true;
      return this.reoPropertiesForm.form.valid && liensValid;
    }
    return false;
  }

  private configureBasedOnScreenSize(windowWidth: number) {
    this.isMobile = windowWidth < 768
  }

  private isSubjectPropertyTheSameAsReo = (reo: RealEstateOwned): boolean => {
    const subjectProperty = this.mortgage?.subjectProperty;
    if (subjectProperty) {
      // LLS-140: normalized match so a formatting difference doesn't misclassify the subject
      // property REO (and is null-safe on partially-entered addresses). Both sides carry a unit,
      // so match on address2 too — otherwise the wrong unit in a building could be flagged.
      return reoMatchesAddress(reo, subjectProperty, { matchUnit: true });
    }
    return false;
  }

  private consolidateReoLiabilities = (): void => {
    syncReoMortgageLiability(this.mortgage, this.reo);
  }
}
